import { useEffect } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";

import { useError } from "@/app-utils/graphql/useError.ts";
import type { InputTableQuerySpec, Notification, PlaybookEvent } from "@/graphql.ts";
import { updateTableRow } from "@/redux/modules/table-data.ts";
import type { IAppState } from "@/redux/reducer.ts";
import type { ReduxDispatch } from "@/redux/redux-utils.ts";
import { useUpdatePlaybookEventGridOnStatusChangeLazyQuery } from "@/ui-modules/playbooks/events/graphql/event-queries.gql.ts";

const useEventNotificationGridUpdate = (tableSpec: InputTableQuerySpec) => {
  const dispatch = useDispatch<ReduxDispatch>();

  const lastNotificationEvent = useSelector<IAppState, PlaybookEvent>(
    (state) => state.notifications.lastNotification?.playbookEvent,
    shallowEqual,
  );

  const [updateEventGrid, { error }] = useUpdatePlaybookEventGridOnStatusChangeLazyQuery();
  void useError(error);

  useEffect(() => {
    if (lastNotificationEvent) {
      updateEventGrid({ variables: { eventId: lastNotificationEvent.id } }).then((updatedEvent) => {
        dispatch(updateTableRow(tableSpec, updatedEvent.data.playbook.event));
      });
    }
  }, [lastNotificationEvent]);
};

/**
 *
 * Hook to update Playbook events related content when receiving status notifications.
 *
 * @param refetch Callback to fetch the required information again, e,g, using dispatch(loadEvent(eventId)) or the
 * refetch function of the apollo API
 * @param eventId Playbook event ID (event.id or job.event.id)
 */
const useNotificationSummaryViewUpdate = (refetch: () => void, eventId: number) => {
  const lastNotification = useSelector<IAppState, Notification>(
    (state) => state.notifications.lastNotification,
    shallowEqual,
  );
  // reload the event summary page if the last notification is related to this event

  useEffect(() => {
    if (lastNotification?.playbookEvent?.id == eventId) {
      refetch();
    }
  }, [lastNotification]);
};

export { useEventNotificationGridUpdate, useNotificationSummaryViewUpdate };
